import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { SearchIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useIngredientCategoriesLookupQuery } from "../../api/ingredient-categories-queries";
import {
  TRANSFER_FILTER_BODY,
  TRANSFER_FILTER_CARD,
} from "../transfers/transferCardLayout";

const SELECT_CLASS = "h-9 w-full min-w-0";

export type StockPoliciesSearch = {
  policyBranch?: string;
  onHandBranchId?: string;
  itemType?: string;
  categoryId?: string;
  search?: string;
  belowMinOnly?: string;
  offset?: number;
  limit?: number;
};

const GLOBAL = "__global__";
const ALL = "__all__";

export function StockPoliciesFilter() {
  const nav = useNavigate({ from: "/app/inventory/stock-policies" });
  const search = useSearch({
    from: "/app/inventory/stock-policies",
  }) as StockPoliciesSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });
  const branches: BranchRow[] = branchesData?.data ?? [];
  const hub = branches.find((b) => b.isDistributionHub) ?? branches[0];
  const categoriesLookup = useIngredientCategoriesLookupQuery();

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const debouncedSearch = useDebounceCallback((v: string) => {
    nav({ search: { ...search, search: v.trim() || undefined, offset: 0 } });
  }, 300);

  useEffect(() => {
    setSearchInput(search.search ?? "");
  }, [search.search]);

  const policyBranchOptions = [
    { value: GLOBAL, label: "ທົ່ວໄປ (Global)" },
    ...branches.map((b) => ({
      value: b.id,
      label: `${b.name} (${b.code})`,
    })),
  ];

  const onHandOptions = branches.map((b) => ({
    value: b.id,
    label: b.isDistributionHub ? `${b.name} (Hub)` : `${b.name} (${b.code})`,
  }));

  const itemTypeOptions = [
    { value: ALL, label: "ທຸກປະເພດ" },
    { value: "INGREDIENT", label: "ວັດຖຸດິບ" },
    { value: "RETAIL_VARIANT", label: "ຕົວແບບສິນຄ້າ" },
  ];

  const categoryOptions = [
    { value: ALL, label: "ທຸກຫມວດ" },
    { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
    ...(categoriesLookup.data ?? []).map((c) => ({
      value: c.id,
      label: c.name,
    })),
  ];

  const hasFilter =
    !!search.search ||
    (search.itemType && search.itemType !== ALL) ||
    (search.categoryId && search.categoryId !== ALL) ||
    search.belowMinOnly === "true";

  const update = (next: Partial<StockPoliciesSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  return (
    <Card className={TRANSFER_FILTER_CARD}>
      <CardContent className={`${TRANSFER_FILTER_BODY} w-full flex-col items-stretch gap-3 py-3`}>
        <div className="grid w-full min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0 space-y-1.5">
            <span className="font-medium text-sm">ເງື່ອນໄຂຕາມ</span>
            <SimpleSelect
              className={SELECT_CLASS}
              value={search.policyBranch ?? GLOBAL}
              onValueChange={(v) => update({ policyBranch: v })}
              options={policyBranchOptions}
            />
          </div>
          <div className="min-w-0 space-y-1.5">
            <span className="font-medium text-sm">ສະແດງຍອດຄົງທີ່</span>
            <SimpleSelect
              className={SELECT_CLASS}
              value={search.onHandBranchId ?? hub?.id ?? ""}
              onValueChange={(v) => update({ onHandBranchId: v })}
              options={onHandOptions}
              placeholder="ເລືອກສາຂາ"
            />
          </div>
          <div className="min-w-0 space-y-1.5">
            <span className="font-medium text-sm">ປະເພດ</span>
            <SimpleSelect
              className={SELECT_CLASS}
              value={search.itemType ?? ALL}
              onValueChange={(v) =>
                update({
                  itemType: v === ALL ? undefined : v,
                  categoryId:
                    v !== "INGREDIENT" ? undefined : search.categoryId,
                })
              }
              options={itemTypeOptions}
            />
          </div>
          <div className="min-w-0 space-y-1.5">
            <span className="font-medium text-sm">ຫມວດວັດຖຸດິບ</span>
            <SimpleSelect
              className={SELECT_CLASS}
              value={search.categoryId ?? ALL}
              disabled={(search.itemType ?? ALL) === "RETAIL_VARIANT"}
              onValueChange={(v) =>
                update({ categoryId: v === ALL ? undefined : v })
              }
              options={categoryOptions}
            />
          </div>
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-3">
          <div className="relative min-w-[12rem] flex-1">
            <SearchIcon className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
            <Input
              className="h-9 pl-9"
              placeholder="ຄົ້ນຫາຊື່ລາຍການ…"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
            />
          </div>
          <label
            htmlFor="stock-policy-below-min"
            className="flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-md px-1 text-sm hover:bg-muted/50"
          >
            <Checkbox
              id="stock-policy-below-min"
              checked={search.belowMinOnly === "true"}
              onCheckedChange={(checked) =>
                update({
                  belowMinOnly: checked === true ? "true" : undefined,
                })
              }
            />
            <span className="leading-none">ສະເພາະຕ່ຳກວ່າເກັນໄຂ</span>
          </label>
          {hasFilter ? (
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
              onClick={() =>
                nav({
                  search: {
                    policyBranch: search.policyBranch ?? GLOBAL,
                    onHandBranchId: search.onHandBranchId ?? hub?.id,
                    offset: 0,
                    limit: search.limit,
                  },
                })
              }
            >
              <XIcon className="size-4" />
              ລ້າງຕົວກອງ
            </button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function stockPoliciesListParams(
  search: StockPoliciesSearch,
  hubBranchId?: string,
) {
  const policyBranch = search.policyBranch ?? "__global__";
  return {
    branchId: policyBranch,
    itemType:
      search.itemType && search.itemType !== "__all__"
        ? (search.itemType as "INGREDIENT" | "RETAIL_VARIANT")
        : undefined,
    categoryId:
      search.categoryId && search.categoryId !== "__all__"
        ? search.categoryId
        : undefined,
    search: search.search,
    belowMinOnly: search.belowMinOnly === "true",
    onHandBranchId: search.onHandBranchId ?? hubBranchId,
    limit: search.limit ?? 50,
    offset: search.offset ?? 0,
  };
}
